import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

/**
 * Vendor redemptions page with QR scanner and manual code entry
 */
function Redemptions() {
  const { t } = useI18n();

  const [showScanner, setShowScanner] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [error, setError] = useState('');
  const [recentRedemptions, setRecentRedemptions] = useState([]);

  const videoRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    loadRecentRedemptions();
    return () => {
      stopScanner();
    };
  }, []);

  async function loadRecentRedemptions() {
    try {
      const data = await api.get('/vendor/dashboard');
      setRecentRedemptions(data.recentRedemptions || []);
    } catch (err) {
      console.error('Failed to load redemptions:', err);
    }
  }

  async function startScanner() {
    setShowScanner(true);
    setError('');

    try {
      const { BrowserQRCodeReader } = await import('@zxing/browser');
      const codeReader = new BrowserQRCodeReader();
      scannerRef.current = codeReader;

      const videoInputDevices = await codeReader.listVideoInputDevices();
      const selectedDeviceId = videoInputDevices[0]?.deviceId;

      if (!selectedDeviceId) {
        setError(t('vendor.noCameraFound'));
        setShowScanner(false);
        return;
      }

      codeReader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, async (result, err) => {
        if (result) {
          const text = result.getText();
          // Extract token from URL or use as-is
          const tokenMatch = text.match(/\/[rc]\/([A-Z0-9]+)/i);
          const token = tokenMatch ? tokenMatch[1] : text;
          await handleRedeem(token);
          stopScanner();
        }
      });
    } catch (err) {
      console.error('Scanner error:', err);
      setError(t('vendor.cameraFailed'));
      setShowScanner(false);
    }
  }

  function stopScanner() {
    if (scannerRef.current) {
      scannerRef.current.reset();
      scannerRef.current = null;
    }
    setShowScanner(false);
  }

  async function handleRedeem(token) {
    setError('');
    setRedemptionResult(null);

    try {
      const result = await api.post(`/vendor/redeem/${token.toUpperCase()}`);
      setRedemptionResult({ success: true, ...result });
      loadRecentRedemptions(); // Refresh list
    } catch (err) {
      if (err.message.includes('already been redeemed')) {
        setRedemptionResult({ success: false, type: 'redeemed', message: t('vendor.alreadyRedeemed') });
      } else if (err.message.includes('expired')) {
        setRedemptionResult({ success: false, type: 'expired', message: t('vendor.couponExpired') });
      } else if (err.message.includes('not for your business')) {
        setRedemptionResult({ success: false, type: 'wrong', message: t('vendor.wrongVendor') });
      } else {
        setRedemptionResult({ success: false, type: 'invalid', message: t('vendor.couponInvalid') });
      }
    }
  }

  async function handleManualRedeem(e) {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await handleRedeem(manualCode.trim());
    setManualCode('');
  }

  return (
    <>
      {/* Redemption result */}
      {redemptionResult && (
        <div className="card" style={{
          background: redemptionResult.success ? '#f0fdf4' : '#fef2f2',
          borderLeft: `4px solid ${redemptionResult.success ? '#22c55e' : '#ef4444'}`
        }}>
          <h3 style={{ color: redemptionResult.success ? '#16a34a' : '#dc2626', marginBottom: '8px' }}>
            {redemptionResult.success ? t('vendor.couponValid') : redemptionResult.message}
          </h3>
          {redemptionResult.success && (
            <>
              <p>{t('vendor.applyDiscount')}</p>
              <p className="text-secondary text-sm mt-2">{redemptionResult.coupon?.offer_title}</p>
            </>
          )}
          <button
            onClick={() => setRedemptionResult(null)}
            className="btn btn-secondary mt-3"
            style={{ padding: '8px 16px' }}
          >
            OK
          </button>
        </div>
      )}

      {/* Scanner */}
      {showScanner ? (
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h3 style={{ fontSize: '16px' }}>{t('vendor.scanCoupon')}</h3>
            <button onClick={stopScanner} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
              {t('common.cancel')}
            </button>
          </div>
          <p className="text-secondary text-sm mb-3">{t('vendor.scanInstructions')}</p>
          <video
            ref={videoRef}
            style={{ width: '100%', borderRadius: '8px', background: '#000' }}
          />
          {error && <p style={{ color: 'var(--error)', marginTop: '8px' }}>{error}</p>}
        </div>
      ) : (
        <button onClick={startScanner} className="btn btn-primary btn-block mb-3" style={{ padding: '16px' }}>
          {t('vendor.scanCoupon')}
        </button>
      )}

      {/* Manual entry */}
      <div className="card">
        <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>{t('vendor.enterCode')}</h3>
        <form onSubmit={handleManualRedeem} className="flex gap-2">
          <input
            type="text"
            className="input"
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            placeholder="ABC123"
            style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'monospace' }}
            maxLength={6}
          />
          <button type="submit" className="btn btn-primary">
            {t('vendor.redeemCoupon')}
          </button>
        </form>
      </div>

      {/* Recent redemptions */}
      {recentRedemptions.length > 0 && (
        <div className="card mt-4">
          <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>{t('vendor.recentRedemptions')}</h3>
          {recentRedemptions.map((r, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: i < recentRedemptions.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div className="flex justify-between">
                <span className="text-sm">{r.offer_title}</span>
                <span className="text-secondary text-xs">{new Date(r.redeemed_at).toLocaleDateString()}</span>
              </div>
              <span className="text-secondary text-xs">{r.property_name}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default Redemptions;
